import { GitCompareArrows, PenLine, Plus, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { EntityPicker } from "@/components/EntityPicker";
import { useApp } from "@/lib/AppContext";
import { today as localToday } from "@/lib/format";
import { ApiError, type Entity, type EntityType, type SubjectRelation } from "@/lib/api";
import { relationChoicesFor, relationTypeMeta, type RelationChoice } from "@/lib/relationTypes";
import { VERDICT_COLOUR } from "@/lib/verdicts";

/**
 * WORLD-7, UI-20 — this subject's relations, read the same way on every entity-backed page: a
 * verdict pill and a source count, the sentence always written subject-first-object-last
 * regardless of which end this page's own entity is on (`isSubject` says which), and the
 * type in words rather than as the stored name (WORLD-10).
 *
 * `editable` is what the page decides. UI-19 made the initiative dossier the one place a
 * relation was typed by hand; UI-20 makes it every page that has one — the World view page, a
 * person's dossier and an entity's detail as well — through the same component, so the add
 * form is one form. It offers WORLD-10's vocabulary from this entity's side ("reports to",
 * "has as a report") and only the directions this entity's kind can stand in, then a typeahead
 * narrowed to the kinds that direction can join, so the Server's refusal is something the form
 * makes hard to reach rather than something it reports.
 */
export function RelationsPanel({
  thisEntityId,
  thisEntityName,
  thisEntityKind,
  relations,
  editable,
  candidates,
  onChanged,
}: {
  thisEntityId: string;
  thisEntityName: string;
  thisEntityKind: EntityType;
  relations: SubjectRelation[];
  editable: boolean;
  /** Every other entity this one could be related to, for the add form. Ignored when
   * `editable` is false. */
  candidates?: Entity[];
  onChanged: () => Promise<void>;
}) {
  return (
    <section>
      <div className="mb-2 flex flex-wrap items-baseline gap-2.5">
        <h2 className="font-semibold text-[var(--text-primary)]">Relations</h2>
        <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
          Stated by sources, maintained across them, or drawn by you.
        </span>
      </div>

      <div className="flex flex-col gap-1.5">
        {relations.length === 0 && (
          <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">
            No relations yet. Sources state them as they are read, a later pass derives them across sources,
            {editable ? " and you can draw one below." : " and you can draw one on the entity's page."}
          </p>
        )}

        {relations.map((relation) => (
          <RelationRow
            key={relation.id}
            relation={relation}
            thisEntityName={thisEntityName}
            editable={editable}
            onChanged={onChanged}
          />
        ))}

        {editable && (
          <AddRelation
            thisEntityId={thisEntityId}
            thisEntityName={thisEntityName}
            thisEntityKind={thisEntityKind}
            candidates={candidates ?? []}
            onChanged={onChanged}
          />
        )}
      </div>
    </section>
  );
}

/** Where an entity opens: a person's dossier, an initiative's, or the entity detail. */
export function entityHref(kind: string, id: string): string {
  if (kind === "person") return `/people/${id}`;
  if (kind === "initiative") return `/initiatives/${id}`;
  return `/entities/${id}`;
}

/** WORLD-8 — the marker a row the owner drew wears on every page, and in the wiki file. */
export function AssertedByYou() {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[var(--vl-bridge-soft)] px-2 py-0.5 text-[length:var(--text-2xs)] font-semibold text-[var(--vl-bridge-soft)]">
      <PenLine size={10} strokeWidth={2.5} />
      Asserted by you
    </span>
  );
}

function RelationRow({
  relation,
  thisEntityName,
  editable,
  onChanged,
}: {
  relation: SubjectRelation;
  thisEntityName: string;
  editable: boolean;
  onChanged: () => Promise<void>;
}) {
  const { api } = useApp();
  const label = relationTypeMeta(relation.type)?.label ?? relation.type.replaceAll("_", " ");
  const other = (
    <Link
      to={entityHref(relation.otherEntityKind, relation.otherEntityId)}
      className="text-[var(--text-primary)] underline decoration-[var(--border-strong)] underline-offset-2 hover:text-[var(--vl-life)]"
    >
      {relation.otherEntityName}
    </Link>
  );

  return (
    <div className="flex items-center gap-2.5 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-3.5 py-2.5 text-[length:var(--text-sm)] hover:border-[var(--border-default)]">
      <span aria-hidden className="shrink-0 text-[var(--vl-virtual-soft)]">
        <GitCompareArrows size={12} strokeWidth={2} />
      </span>

      <span className="min-w-0 flex-1 text-[var(--text-primary)]">
        {relation.isSubject ? (
          <>
            {thisEntityName} {label} {other}
          </>
        ) : (
          <>
            {other} {label} {thisEntityName}
          </>
        )}
      </span>

      {relation.assertedByUser && <AssertedByYou />}

      <span
        className="shrink-0 rounded-full border px-2 py-0.5 text-[length:var(--text-2xs)] font-semibold"
        style={{ borderColor: VERDICT_COLOUR[relation.verdict], color: VERDICT_COLOUR[relation.verdict] }}
      >
        {relation.verdict}
      </span>

      <span className="shrink-0 text-[length:var(--text-2xs)] text-[var(--text-muted)]">
        {relation.sourceCount} {relation.sourceCount === 1 ? "source" : "sources"}
      </span>

      {editable && (
        <button
          type="button"
          aria-label="Remove relation"
          onClick={async () => {
            await api.removeRelation(relation.id);
            await onChanged();
          }}
          className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[var(--radius-sm)] border border-transparent text-[var(--text-muted)] opacity-50 hover:border-[var(--border-subtle)] hover:text-[var(--destructive)] hover:opacity-100"
        >
          <X size={12} strokeWidth={2} />
        </button>
      )}
    </div>
  );
}

/**
 * The add form, exported for the person dossier, whose Relations section keeps UI-8's own rows
 * (with their sources disclosure and undo) and takes this beneath them.
 */
export function AddRelation({
  thisEntityId,
  thisEntityName,
  thisEntityKind,
  candidates,
  onChanged,
}: {
  thisEntityId: string;
  thisEntityName: string;
  thisEntityKind: EntityType;
  candidates: Entity[];
  onChanged: () => Promise<void>;
}) {
  const { api } = useApp();
  const choices = useMemo(() => relationChoicesFor(thisEntityKind), [thisEntityKind]);

  const [adding, setAdding] = useState(false);
  const [choiceKey, setChoiceKey] = useState("");
  const [other, setOther] = useState<Entity | null>(null);
  const [validFrom, setValidFrom] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A term is a word, not a thing, and no relation joins one.
  if (choices.length === 0) return null;

  const choice: RelationChoice | undefined = choices.find((entry) => entry.key === choiceKey);

  if (!adding) {
    return (
      <button
        type="button"
        onClick={() => setAdding(true)}
        className="inline-flex w-fit items-center gap-1.5 rounded-full border border-dashed border-[var(--border-default)] px-2.5 py-1 text-[length:var(--text-xs)] text-[var(--text-muted)] hover:border-[var(--primary)] hover:text-[var(--vl-bridge-soft)]"
      >
        <Plus size={12} strokeWidth={2} />
        Add relation
      </button>
    );
  }

  function reset() {
    setAdding(false);
    setChoiceKey("");
    setOther(null);
    setValidFrom("");
    setProblem(null);
  }

  async function submit() {
    if (!choice || !other || busy) return;

    setBusy(true);
    setProblem(null);
    try {
      await api.assertRelation(thisEntityId, {
        relationType: choice.type.name,
        otherEntityId: other.id,
        thisIsSubject: choice.thisIsSubject,
        // Today is "from now", as on a belief (BeliefEditor): a bare date is midnight.
        ...(validFrom && validFrom !== localToday() ? { validFrom } : {}),
      });
      reset();
      await onChanged();
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The relation could not be saved.");
    } finally {
      setBusy(false);
    }
  }

  const preview =
    choice && other
      ? choice.thisIsSubject
        ? `${thisEntityName} ${choice.type.label} ${other.name}`
        : `${other.name} ${choice.type.label} ${thisEntityName}`
      : null;

  return (
    <div className="flex flex-col gap-2.5 rounded-[var(--radius-md)] border border-dashed border-[var(--border-default)] px-3.5 py-3">
      <div className="flex flex-wrap items-center gap-2 text-[length:var(--text-sm)]">
        <span className="max-w-[18ch] truncate text-[length:var(--text-xs)] font-medium text-[var(--text-secondary)]">
          {thisEntityName}
        </span>

        <select
          value={choiceKey}
          aria-label="Relation type"
          onChange={(event) => {
            const next = choices.find((entry) => entry.key === event.target.value);
            setChoiceKey(event.target.value);
            // A picked entity the new type cannot join is cleared rather than left to be refused.
            if (other && next && !next.otherKinds.includes(other.type)) setOther(null);
          }}
          className="h-8 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-2)] px-2 text-[length:var(--text-xs)] text-[var(--text-primary)]"
        >
          <option value="">Choose a relation…</option>
          {choices.map((entry) => (
            <option key={entry.key} value={entry.key}>
              {entry.label}
            </option>
          ))}
        </select>

        <EntityPicker
          entities={candidates}
          kinds={choice?.otherKinds}
          excludeId={thisEntityId}
          value={other}
          onChange={setOther}
        />
      </div>

      <div className="flex flex-wrap items-center gap-x-[var(--space-4)] gap-y-2 text-[length:var(--text-xs)] text-[var(--text-muted)]">
        <label className="inline-flex items-center gap-1.5">
          Holding since
          <input
            type="date"
            value={validFrom}
            max={localToday()}
            onChange={(event) => setValidFrom(event.target.value)}
            aria-label="Relation valid from"
            className="h-7 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-2)] px-2 text-[length:var(--text-xs)] text-[var(--text-primary)]"
          />
          {!validFrom && <span>(today)</span>}
        </label>
        {preview && <span className="text-[var(--text-secondary)]">“{preview}”</span>}
      </div>

      <div className="flex items-center gap-2">
        {problem && (
          <span role="alert" className="text-[length:var(--text-2xs)] text-[var(--warning)]">
            {problem}
          </span>
        )}
        <span className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={reset}
            className="h-7 rounded-[var(--radius-md)] px-2 text-[length:var(--text-xs)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || !choice || !other}
            onClick={() => void submit()}
            className="inline-flex h-7 items-center rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-xs)] font-medium text-[var(--primary-foreground)] disabled:opacity-50"
          >
            Add
          </button>
        </span>
      </div>
    </div>
  );
}
